import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { IntegrationApi } from '@core/services/integrations/integration.api';
import { IntegrationState } from '@core/services/integrations/integration.state';
import {
	IIntegrationsResponse,
	IIntegrationTopic,
	ISaveTopicSubscriptionPayload,
	ITopicSubscription,
} from '@core/services/integrations/integrations.interface';
import { IResponseMessage } from '@app/interfaces';

@Injectable({
	providedIn: 'root',
})
export class IntegrationService {
	constructor(private api: IntegrationApi, private state: IntegrationState) {}

	isUpdating$(): Observable<boolean> {
		return this.state.isUpdating$();
	}

	hasSlack(): Observable<boolean> {
		return this.state.hasSlack();
	}

	hasTeams(): Observable<boolean> {
		return this.state.hasTeams();
	}

	checkIntegrations(org_id: string): Observable<IIntegrationsResponse> {
		this.state.setUpdating(true);

		return this.api.checkIntegrations(org_id).pipe(
			tap(({ slack, teams }) => {
				this.state.setSlack(slack);
				this.state.setTeams(teams);
			}),
			catchError((error) => {
				console.error('API checkIntegrations: ', error);
				return of(null);
			}),
			finalize(() => this.state.setUpdating(false)),
		);
	}

	loadTopics(): Observable<IIntegrationTopic[]> {
		this.state.setUpdating(true);

		return this.api.getTopicSubscriptions().pipe(
			switchMap((data) =>
				this.api.getNotificationTopics().pipe(
					map((topics) => {
						const keys = !!data?.subscriptions ? Object.keys(data?.subscriptions) : [];
						if (keys.length) {
							return keys.map((item) => ({
								topic: topics[item],
								channel: data.subscriptions[item].channel,
								destination: data.subscriptions[item].type,
							}));
						}
						return [];
					}),
				),
			),
			tap((value) => {
				this.state.setTopics(value);
				this.state.setUpdating(false);
			}),
		);
	}

	getTopicSubscriptions(): Observable<ITopicSubscription> {
		return this.api.getTopicSubscriptions();
	}

	getNotificationTopics(): Observable<{ [p: string]: string }> {
		return this.api.getNotificationTopics();
	}

	getTopics(): Observable<IIntegrationTopic[]> {
		return this.state.getTopics();
	}

	getTopicsValue(): IIntegrationTopic[] {
		return this.state.getTopicsValue();
	}

	getSlackChannels(): Observable<string[]> {
		return this.api.getSlackChannels().pipe(catchError(() => of([])));
	}

	integrateTeams(hook: string, id: string): Observable<IResponseMessage> {
		return this.api.integrateTeams(hook, id);
	}

	integrateSlack(code: string, org_id: string, redirect_uri: string): Observable<IResponseMessage> {
		return this.api.integrateSlack(code, org_id, redirect_uri);
	}

	removeIntegration(integration: string): Observable<IResponseMessage> {
		return this.api.removeIntegration(integration);
	}

	saveTopicSubscriptions(topics: ISaveTopicSubscriptionPayload): Observable<IResponseMessage> {
		return this.api.saveTopicSubscriptions(topics);
	}
}
